// custom: T8a PR3b — camada server-side da busca híbrida de conversas
//
// Responsabilidade: dado um searchQuery reativo, dispara SearchAPI.conversations
// com debounce 300ms e retorna { serverResults, isSearching, searchError }.
// Cancelamento duplo:
//   1. debounceTimer manual (clearTimeout) — cancela a chamada ainda não disparada
//   2. AbortController — aborta fetch já em voo quando nova query chega ou busca limpa
//
// NÃO usa a store conversationSearch (tem estado compartilhado com a busca
// global e acumularia resultados entre sessões de busca).

import { ref, watch } from 'vue';
import SearchAPI from 'dashboard/api/search';
// custom: T8a PR3b — normaliza shape da SearchAPI para o shape do item da lista
import { normalizeSearchResult } from 'dashboard/composables/cw_custom/searchResultNormalizer';

// Mínimo de caracteres para acionar a busca server-side
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * Composable de busca server-side de conversas.
 *
 * @param {import('vue').Ref<string>} searchQuery - ref reativa com a query atual
 * @returns {{
 *   serverResults: import('vue').Ref<Array>,
 *   isSearching: import('vue').Ref<boolean>,
 *   searchError: import('vue').Ref<boolean>,
 *   retrySearch: () => void,
 *   cancelSearch: () => void,
 * }}
 */
export function useConversationSearch(searchQuery) {
  const serverResults = ref([]);
  const isSearching = ref(false);
  const searchError = ref(false);

  let debounceTimer = null;
  let currentAbort = null;

  function cancelSearch() {
    // Cancela debounce pendente
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    // Aborta fetch em voo
    if (currentAbort) {
      currentAbort.abort();
      currentAbort = null;
    }
    serverResults.value = [];
    isSearching.value = false;
    searchError.value = false;
  }

  async function fetchServerResults(q) {
    // Aborta requisição anterior se ainda estiver em andamento
    if (currentAbort) {
      currentAbort.abort();
    }
    const abort = new AbortController();
    currentAbort = abort;

    isSearching.value = true;
    searchError.value = false;

    try {
      const response = await SearchAPI.conversations({
        q,
        signal: abort.signal,
      });
      if (abort.signal.aborted) return;
      const raw = response?.data?.payload?.conversations ?? [];
      // Normaliza do shape da SearchAPI para o shape do item da store
      serverResults.value = raw.map(normalizeSearchResult).filter(Boolean);
    } catch (err) {
      if (abort.signal.aborted) return;
      searchError.value = true;
      serverResults.value = [];
    } finally {
      if (!abort.signal.aborted) {
        isSearching.value = false;
        if (currentAbort === abort) currentAbort = null;
      }
    }
  }

  function retrySearch() {
    const trimmed = searchQuery.value.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;

    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    fetchServerResults(trimmed);
  }

  watch(
    searchQuery,
    q => {
      const trimmed = q.trim();

      if (trimmed.length < MIN_QUERY_LENGTH) {
        cancelSearch();
        return;
      }

      // Cancela debounce anterior e agenda novo
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        fetchServerResults(trimmed);
      }, DEBOUNCE_MS);
    },
    { immediate: false }
  );

  return {
    serverResults,
    isSearching,
    searchError,
    retrySearch,
    cancelSearch,
  };
}
